import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ApiHelper } from '../utils/ApiHelper';
import { COMPANY_DETAIL_OF_USER } from '../lib/API_KEYS';

const CompanyContext = createContext();

export const CompanyProvider = ({ children }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFetched, setIsFetched] = useState(false);

  /**
   * Fetch company details with caching
   * Only makes API call once, subsequent calls use cached data
   */
  const fetchCompanyDetails = useCallback(async () => {
    // Return cached data if already fetched
    if (isFetched && company) {
      return company;
    }

    // Skip if already loading
    if (loading) {
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await new ApiHelper().FetchFromServer(...COMPANY_DETAIL_OF_USER);
      if (response?.data) {
        setCompany(response.data);
        setIsFetched(true);
        return response.data;
      }
    } catch (err) {
      console.error('Error fetching company details:', err);
      setError(err?.message || 'Failed to fetch company details');
      setIsFetched(true); // Mark as attempted even on error to prevent retry spam
    } finally {
      setLoading(false);
    }
  }, [isFetched, company, loading]);

  /**
   * Reset company data (useful on logout)
   */
  const resetCompany = useCallback(() => {
    setCompany(null);
    setError(null);
    setLoading(false);
    setIsFetched(false);
  }, []);

  /**
   * Update company data locally (useful after company updates)
   */
  const updateCompanyLocal = useCallback((updates) => {
    setCompany(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const value = {
    company,
    loading,
    error,
    fetchCompanyDetails,
    setCompany,
    resetCompany,
    updateCompanyLocal,
    logo: company?.logo,
    companyName: company?.name,
    companyId: company?._id,
    isFetched,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return context;
};

